import { Controller } from "stimulus";
import { formatCurrency } from "../../util/currency";
import { debounce, throttle } from "../../util";

const EXPONENT_CONFIG = { significantFigures: 4, maximumDecimalTrailingZeroes: 4 };

export default class extends Controller {
  static targets = [
    "coinInputContainer", "currencyInputContainer", "swapAnchor",
    "coinInput", "currencyInput", "amountInput",
    "convertedAmount", "lastUpdated",
  ];

  connect() {
    this.handleSearch = debounce(this._handleSearch.bind(this), 300);

    this.coinSearchUrl = this.element.dataset.coinSearchUrl;
    this.defaultCoinsData = JSON.parse(this.element.dataset.defaultCoins || "null");
    this.searchedCoinsData = [];

    this.handleCurrencyChange();
    this.handleCoinChange();
  }

  handleCoinChange() {
    const coinId = +this.coinInputTarget.value;
    this.selectedCoin = this.searchedCoinsData.find(x => x.id === coinId) || this.defaultCoinsData.find(x => x.id === coinId);

    this.calculate();
  }

  handleCurrencyChange() {
    this.selectedCurrency = this.currencyInputTarget.value.toLowerCase();
    this.calculate();
  }

  calculate() {
    if (!this.selectedCoin || !this.selectedCurrency) {
      return;
    }

    const price = +this.selectedCoin.price[this.selectedCurrency];
    const amount = +this.amountInputTarget.value;

    if (price && amount) {
      const result = this.swapped ? amount / price : amount * price;
      const source = this.swapped ? this.selectedCurrency : this.selectedCoin.symbol;
      const target = this.swapped ? this.selectedCoin.symbol : this.selectedCurrency;
      const exponentConfig =  (result > 0 && result < 1) ? EXPONENT_CONFIG : false;

      // TODO: Replace with I18n.t JS string after translated
      // this.lastUpdatedTarget.innerHTML = I18n.t("labels.last_updated_at", { updated_at: this.selectedCoin.updated });
      this.lastUpdatedTarget.innerHTML = this.element.dataset.lastUpdatedText.replace("%{updated_at}", this.selectedCoin.updated);

      this.convertedAmountTarget.innerHTML = `${formatCurrency(amount, source)} = ${formatCurrency(result, target, false, exponentConfig)}`;
      this.convertedAmountTarget.parentNode.classList.remove("tw-invisible");
    } else {
      this.lastUpdatedTarget.innerHTML = "&nbsp;";
      this.convertedAmountTarget.innerHTML = "&nbsp;";
      this.convertedAmountTarget.parentNode.classList.add("tw-invisible");
    }
  }

  swap() {
    if (!this.swapped) {
      this.coinInputContainerTarget.parentNode.insertBefore(this.coinInputContainerTarget, this.currencyInputContainerTarget);
      this.currencyInputContainerTarget.parentNode.insertBefore(this.currencyInputContainerTarget, this.swapAnchorTarget);
    } else {
      this.currencyInputContainerTarget.parentNode.insertBefore(this.currencyInputContainerTarget, this.coinInputContainerTarget);
      this.coinInputContainerTarget.parentNode.insertBefore(this.coinInputContainerTarget, this.swapAnchorTarget);
    }

    this.swapped = !this.swapped;
    this.calculate();
  }

  _handleSearch(e) {
    // Keep track of search counts to prevent race conditions due to slow network requests.
    this._searchCount = this._searchCount || 0;
    this._searchCount += 1;
    const searchCount = this._searchCount;

    const url = new URL(this.coinSearchUrl);
    url.searchParams.set("query", e.detail.query);

    fetch(url, { credentials: "same-origin" })
      .then(async response => {
        if (searchCount !== this._searchCount) {
          return;
        }

        if (!response.ok) {
          Toaster.toast(I18n.t("metamask.unknown_error"), { icon: { classes: "fas fa-times tw-text-danger-500" } });
        }

        const data = await response.json();
        if (!data?.length) {
          this.searchedCoinsData = null;
          return e.detail.showDropdownMessage();
        }

        this.searchedCoinsData = data;
        e.detail.clearDropdownItems();

        data.forEach(coin => {
          const innerHTML = `
            <div class="tw-flex tw-items-center tw-gap-x-2">
              <img class="tw-h-6 tw-w-6" src="${coin.thumbnail}"/>
              ${coin.name} (${coin.symbol.toUpperCase()})
            </div>
          `;

          e.detail.addDropdownItem(coin.id, innerHTML);
        });
      });
  }
}
